/**
 * i18n — EN / ID bilingual layer.
 *
 * Merged from the-empire (XENO), whose identity was a bilingual
 * "Aplikasi AI Indonesia". Strings are keyed flat; `t(key, fallback)`
 * returns the active language, falling back to English, then to the
 * provided fallback, then to the key itself.
 *
 * App names/descriptions are keyed `app.<id>.name` / `app.<id>.desc`
 * so the registry's canonical English stays intact for categorisation
 * while the UI can render localised labels.
 */
import { useStore, type Lang } from './store'

type Entry = { en: string; id: string }

const DICT: Record<string, Entry> = {
  // ── Shell ──
  'shell.apps':     { en: 'apps', id: 'aplikasi' },
  'shell.running':  { en: 'running', id: 'berjalan' },
  'shell.search':   { en: 'Search apps, commands…', id: 'Cari aplikasi, perintah…' },
  'shell.start':    { en: 'The Empire', id: 'The Empire' },
  'shell.language': { en: 'Language', id: 'Bahasa' },

  // ── The Bridge (living home) ──
  'home.greet.morning':   { en: 'Good morning', id: 'Selamat pagi' },
  'home.greet.afternoon': { en: 'Good afternoon', id: 'Selamat siang' },
  'home.greet.evening':   { en: 'Good evening', id: 'Selamat sore' },
  'home.greet.night':     { en: 'Good night', id: 'Selamat malam' },
  'home.ask':             { en: 'Ask Cakra anything…', id: 'Tanya Cakra apa saja…' },
  'home.ask.label':       { en: 'Ask Cakra', id: 'Tanya Cakra' },
  'home.today':           { en: 'Today', id: 'Hari Ini' },
  'home.today.free':      { en: 'no events — clear sky', id: 'tanpa acara — langit cerah' },
  'home.tasks':           { en: 'Open Tasks', id: 'Tugas Terbuka' },
  'home.tasks.clear':     { en: 'all clear', id: 'semua beres' },
  'home.goals':           { en: 'Goals', id: 'Target' },
  'home.goals.none':      { en: 'none set yet', id: 'belum ada target' },
  'home.goals.avg':       { en: 'avg progress', id: 'rata-rata progres' },
  'home.organism':        { en: 'Organism', id: 'Organisme' },
  'home.links':           { en: 'links', id: 'tautan' },
  'home.continue':        { en: 'Jump back in', id: 'Lanjutkan' },

  // ── The Bridge · Attention feed (one reason string per AttentionKind) ──
  'home.attention':          { en: 'Needs you', id: 'Perlu perhatian' },
  'home.attention.clear':    { en: 'All clear — nothing needs you', id: 'Semua beres — tak ada yang perlu perhatian' },
  'attention.task-overdue':  { en: 'Overdue', id: 'Terlambat' },
  'attention.event-today':   { en: 'Today', id: 'Hari ini' },
  'attention.task-open':     { en: 'To do', id: 'Perlu dikerjakan' },
  'attention.goal-stalled':  { en: 'Stalled', id: 'Tersendat' },
  'attention.reading':       { en: 'Reading', id: 'Sedang dibaca' },
  'attention.handoff':       { en: 'Handed to you', id: 'Diberikan ke kamu' },
  'attention.act.done':      { en: 'Mark done', id: 'Tandai selesai' },
  'attention.act.dismiss':   { en: 'Dismiss', id: 'Abaikan' },
  // Shell attention badge — the cockpit reaches beyond the home (EPIC-18).
  'shell.home':              { en: 'Home', id: 'Beranda' },
  'shell.attention.short':   { en: 'need you', id: 'perlu perhatian' },

  // ── Network app ──
  'network.title':  { en: 'The Network', id: 'Jaringan Hidup' },
  'network.hint':   { en: 'CORE wired to every instrument — click a node to open it', id: 'INTI terhubung ke tiap instrumen — klik node untuk membuka' },
  'network.signal': { en: 'signal', id: 'sinyal' },
  'network.live':   { en: 'Live Signal', id: 'Sinyal Langsung' },
  'network.awaiting': { en: 'awaiting signal…', id: 'menunggu sinyal…' },

  // ── Associative constellation (EPIC-19 · the 6th lens) ──
  'related.title':          { en: 'Related', id: 'Terkait' },
  'related.reason.linked':     { en: 'linked', id: 'tertaut' },
  'related.reason.sharedTag':  { en: 'shared tag', id: 'tag sama' },
  'related.reason.sharedTerm': { en: 'shared term', id: 'istilah sama' },
  'related.reason.sameDay':    { en: 'same day', id: 'hari sama' },

  // ── Artifacts (Cakra-generated) ──
  'artifacts.building':     { en: 'Building artifact…', id: 'Membuat artefak…' },
  'artifacts.interrupted':  { en: 'Interrupted — partial content', id: 'Terputus — konten sebagian' },
  'artifacts.ready':        { en: 'Ready · tap to open', id: 'Siap · ketuk untuk buka' },
  'artifacts.open':         { en: 'Open artifact', id: 'Buka artefak' },
  'artifacts.copy':         { en: 'Copy source', id: 'Salin sumber' },
  'artifacts.close':        { en: 'Close', id: 'Tutup' },
  'artifacts.save':         { en: 'Save', id: 'Simpan' },
  'artifacts.saved':        { en: 'Saved', id: 'Tersimpan' },
  'artifacts.generated':    { en: 'Generated by Cakra', id: 'Dibuat oleh Cakra' },
  'artifacts.rename':       { en: 'Rename', id: 'Ganti nama' },
  'artifacts.renamePrompt': { en: 'Rename artifact', id: 'Ganti nama artefak' },
  'artifacts.delete':       { en: 'Delete', id: 'Hapus' },

  // ── App names (mapped onto empire's registry ids) ──
  'app.calculator.name':       { en: 'Calculator', id: 'Kalkulator' },
  'app.calendar.name':         { en: 'Calendar', id: 'Kalender' },
  'app.clock.name':            { en: 'Clock', id: 'Jam' },
  'app.weather.name':          { en: 'Weather', id: 'Cuaca' },
  'app.grammar.name':          { en: 'Grammar Fix', id: 'Tata Bahasa' },
  'app.language.name':         { en: 'Language Lab', id: 'Belajar Bahasa' },
  'app.music.name':            { en: 'Music', id: 'Musik' },
  'app.video.name':            { en: 'Video', id: 'Video' },
  'app.files.name':            { en: 'Files', id: 'Berkas' },
  'app.cache.name':            { en: 'Cache Cleaner', id: 'Pembersih' },
  'app.browser.name':          { en: 'Browser', id: 'Peramban' },
  'app.editor.name':           { en: 'Code Editor', id: 'Editor Kode' },
  'app.notes.name':            { en: 'Notes', id: 'Catatan' },
  'app.photos.name':           { en: 'Photos', id: 'Foto' },
  'app.datacenter.name':       { en: 'Data Center', id: 'Pusat Data' },
  'app.maps.name':             { en: 'Maps', id: 'Peta' },
  'app.messages.name':         { en: 'Messages', id: 'Pesan' },
  'app.prompt-generator.name': { en: 'Prompt Gen', id: 'Pembuat Prompt' },
  'app.token-counter.name':    { en: 'Token Counter', id: 'Penghitung Token' },
  'app.learning-tracker.name': { en: 'Learning Tracker', id: 'Pelacak Belajar' },
  'app.ai-chat.name':          { en: 'Cakra', id: 'Cakra' },
  'app.solver.name':           { en: 'Problem Solver', id: 'Pemecah Masalah' },
  'app.artifacts.name':        { en: 'Artifacts', id: 'Artefak' },
  'app.network.name':          { en: 'Network', id: 'Jaringan' },
}

export function translate(lang: Lang, key: string, fallback?: string): string {
  const entry = DICT[key]
  if (entry) return entry[lang] || entry.en
  return fallback ?? key
}

/** Hook: subscribes to the active language and returns a bound `t`. */
export function useLang() {
  const lang = useStore(s => s.lang)
  const toggleLang = useStore(s => s.toggleLang)
  const t = (key: string, fallback?: string) => translate(lang, key, fallback)
  return { lang, t, toggleLang }
}
